import { useCallback, useMemo } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'

import { templateDetailAtom } from './atom'
import { Choice } from '../../../../../../entities/choice/entity'
import { MediumItem } from '../../../../../../entities/medium-item/entity'
import { SmallItem } from '../../../../../../entities/small-item/entity'

export const useEditCard = (index: number) => {
  const setAtom = useSetAtom(templateDetailAtom)
  const { mediumItems } = useAtomValue(templateDetailAtom)

  const v2 = true

  const onAdd = useCallback(
    () =>
      setAtom({
        type: 'addSmallItem',
        mediumItemIndex: index,
        v2,
      }),
    [setAtom, index, v2],
  )

  const canUpMediumItem = useMemo(() => {
    return !!mediumItems.find(mi => mediumItems.indexOf(mi) < index)
  }, [index, mediumItems])

  const canDownMediumItem = useMemo(() => {
    return !!mediumItems.find(mi => mediumItems.indexOf(mi) > index)
  }, [index, mediumItems])

  const onUpMediumItem = useCallback(() => {
    setAtom({
      type: 'changeIndexMediumItem',
      index,
      direction: 'up',
    })
  }, [setAtom, index])

  const onDownMediumItem = useCallback(() => {
    setAtom({
      type: 'changeIndexMediumItem',
      index,
      direction: 'down',
    })
  }, [setAtom, index])

  const onChangeMediumItem = useCallback(
    (values: Partial<MediumItem>) => {
      setAtom({
        type: 'updateMediumItem',
        index,
        values,
      })
    },
    [setAtom, index],
  )

  const onDeleteMediumItem = useCallback(() => {
    setAtom({
      type: 'deleteMediumItem',
      index,
    })
  }, [setAtom, index])

  const onChangeSmallItem = useCallback(
    ({
      smallItemUid,
      values,
    }: {
      smallItemUid: string
      values: Partial<SmallItem>
    }) => {
      setAtom({
        type: 'updateSmallItem',
        mediumItemIndex: index,
        uid: smallItemUid,
        values,
      })
    },
    [setAtom, index],
  )

  const canChangeSmallItemIndex = useCallback(
    ({ smallItemUid }: { smallItemUid: string }) => {
      const mediumItem = mediumItems[index]
      const rootSmallItems = Object.values(mediumItem.smallItemMap).filter(
        si => si.parentUid === null,
      )
      const smallItem = mediumItem.smallItemMap[smallItemUid]

      return {
        canUp: rootSmallItems.some(si => si.index < smallItem.index),
        canDown: rootSmallItems.some(si => si.index > smallItem.index),
      }
    },
    [index, mediumItems],
  )

  const onChangeSmallItemIndex = useCallback(
    ({
      smallItemUid,
      direction,
    }: {
      smallItemUid: string
      direction: 'up' | 'down'
    }) => {
      setAtom({
        type: 'changeIndexSmallItem',
        mediumItemIndex: index,
        uid: smallItemUid,
        direction,
      })
    },
    [setAtom, index],
  )

  const onRemoveSmallItem = useCallback(
    ({ smallItemUid }: { smallItemUid: string }) => {
      setAtom({
        type: 'deleteSmallItem',
        mediumItemIndex: index,
        uid: smallItemUid,
      })
    },
    [setAtom, index],
  )

  const onAddChoice = useCallback(
    ({ smallItemUid }: { smallItemUid: string }) => {
      setAtom({ type: 'addChoice', mediumItemIndex: index, smallItemUid })
    },
    [setAtom, index],
  )

  const onChangeChoice = useCallback(
    ({
      smallItemUid,
      choiceIndex,
      values,
    }: {
      smallItemUid: string
      choiceIndex: number
      values: Partial<Choice>
    }) => {
      setAtom({
        type: 'updateChoice',
        mediumItemIndex: index,
        smallItemUid,
        choiceIndex,
        values,
      })
    },
    [setAtom, index],
  )

  const onRemoveChoice = useCallback(
    ({
      smallItemUid,
      choiceIndex,
    }: {
      smallItemUid: string
      choiceIndex: number
    }) => {
      setAtom({
        type: 'deleteChoice',
        mediumItemIndex: index,
        smallItemUid,
        choiceIndex,
      })
    },
    [setAtom, index],
  )

  const onAddSmallItemToChoice = useCallback(
    ({
      smallItemUid,
      choiceIndex,
    }: {
      smallItemUid: string
      choiceIndex: number
    }) => {
      setAtom({
        type: 'addSmallItemToChoice',
        mediumItemIndex: index,
        smallItemUid,
        choiceIndex,
        v2,
      })
    },
    [setAtom, index, v2],
  )

  return {
    onAdd,
    canUpMediumItem,
    canDownMediumItem,
    onUpMediumItem,
    onDownMediumItem,
    onChangeMediumItem,
    onDeleteMediumItem,
    onChangeSmallItem,
    canChangeSmallItemIndex,
    onChangeSmallItemIndex,
    onRemoveSmallItem,
    onAddChoice,
    onChangeChoice,
    onRemoveChoice,
    onAddSmallItemToChoice,
  }
}
